import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationFeedback } from '@/core/components/ValidationFeedback/ValidationFeedback';
import type { ImageColorSpace } from '@/features/scheduler/utils/imageConversion';
import type { CarouselContainerPayload } from '@/features/scheduler/services/SaveScheduledPost';
import {
  BYTES_PER_MEGABYTE,
  CAROUSEL_MAX_IMAGE_BYTES,
  CAROUSEL_MAX_ITEMS,
  CAROUSEL_MAX_VIDEO_BYTES,
  CAROUSEL_MIN_ITEMS,
  isSupportedCarouselVideo,
  prepareCarouselImage,
} from '@/features/scheduler/utils/carouselMedia';
import { ImageEditor } from './ImageEditor/ImageEditor';
import { getCarouselUploadConfig } from './uploadConfigs';
import './CarouselContent.scss';

export interface CarouselMediaItem {
  id: string;
  file: File;
  fileType: 'image' | 'video';
  previewUrl: string;
  sourceName: string;
  width?: number;
  height?: number;
  colorSpace?: ImageColorSpace;
  error?: string;
  status: 'processing' | 'ready' | 'error';
  containerId?: string;
  containerPayload?: CarouselContainerPayload;
  containerCreatedAt?: number;
  retryStatus?: string;
  retryError?: string;
  isAiGenerated?: boolean;
}

interface CarouselContentProps {
  items: CarouselMediaItem[];
  onItemsChange?: (items: CarouselMediaItem[]) => void;
  onOverflow?: () => void;
  onCaptionChange?: (caption: string) => void;
  caption?: string;
  showCaption?: boolean;
  disabled?: boolean;
}

const getFileType = (file: File): CarouselMediaItem['fileType'] =>
  file.type.startsWith('video/') || /\.(mp4|mov)$/i.test(file.name) ? 'video' : 'image';
const FILE_SIZE_DECIMAL_PLACES = 2;
const fileSize = (size: number): string => `${(size / BYTES_PER_MEGABYTE).toFixed(FILE_SIZE_DECIMAL_PLACES)} MB`;

export const CarouselContent: React.FC<CarouselContentProps> = ({
  items,
  onItemsChange,
  onOverflow,
  onCaptionChange,
  caption = '',
  showCaption = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const captionRef = useRef<HTMLElement | null>(null);
  const addMoreInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replacementItemIdRef = useRef<string | null>(null);
  const editModalRef = useRef<HTMLElement | null>(null);
  const itemsRef = useRef(items);
  const onItemsChangeRef = useRef(onItemsChange);
  const onOverflowRef = useRef(onOverflow);
  const previewUrlsRef = useRef(new Set<string>());
  const isMountedRef = useRef(true);
  const [overflowFiles, setOverflowFiles] = useState<string[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const uploadConfig = getCarouselUploadConfig(t);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    onItemsChangeRef.current = onItemsChange;
    onOverflowRef.current = onOverflow;
  }, [onItemsChange, onOverflow]);

  const createPreviewUrl = useCallback((file: File): string => {
    const url = URL.createObjectURL(file);
    previewUrlsRef.current.add(url);
    return url;
  }, []);

  const revokePreviewUrl = useCallback((url: string) => {
    URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(url);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const previewUrls = previewUrlsRef.current;
    return () => {
      isMountedRef.current = false;
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.clear();
    };
  }, []);

  useEffect(() => {
    const activePreviewUrls = new Set(items.map((item) => item.previewUrl));
    previewUrlsRef.current.forEach((url) => {
      if (!activePreviewUrls.has(url)) revokePreviewUrl(url);
    });
  }, [items, revokePreviewUrl]);

  const updateItems = useCallback((update: (current: CarouselMediaItem[]) => CarouselMediaItem[]) => {
    const next = update(itemsRef.current);
    itemsRef.current = next;
    onItemsChangeRef.current?.(next);
  }, []);

  const setItemError = useCallback((id: string, error: string, sourceFile?: File) => {
    updateItems((current) => current.map((item) => item.id === id && (sourceFile === undefined || item.file === sourceFile)
      ? { ...item, error, status: 'error' }
      : item));
  }, [updateItems]);

  const processImage = useCallback(async (id: string, sourceFile: File) => {
    try {
      const result = await prepareCarouselImage(sourceFile);
      if (!isMountedRef.current) return;
      const error = result.file.size > CAROUSEL_MAX_IMAGE_BYTES ? t('carousel.imageTooLarge') : undefined;
      updateItems((current) => current.map((item) => {
        if (item.id !== id || item.file !== sourceFile) return item;
        revokePreviewUrl(item.previewUrl);
        return {
          ...item,
          file: result.file,
          previewUrl: createPreviewUrl(result.file),
          width: result.width,
          height: result.height,
          colorSpace: result.colorSpace,
          error,
          status: error ? 'error' : 'ready',
          containerId: undefined,
          containerPayload: undefined,
          retryStatus: undefined,
          retryError: undefined,
        };
      }));
    } catch (error) {
      console.error('Carousel image conversion failed:', error);
      if (isMountedRef.current) setItemError(id, t('carousel.conversionError'), sourceFile);
    }
  }, [createPreviewUrl, revokePreviewUrl, setItemError, t, updateItems]);

  const processSelectedFile = useCallback((item: CarouselMediaItem) => {
    if (item.fileType === 'image') {
      void processImage(item.id, item.file);
      return;
    }
    if (item.file.size > CAROUSEL_MAX_VIDEO_BYTES) {
      setItemError(item.id, t('carousel.videoTooLarge'), item.file);
      return;
    }
    if (!isSupportedCarouselVideo(item.file)) {
      setItemError(item.id, t('carousel.unsupportedVideo'), item.file);
      return;
    }
    updateItems((current) => current.map((currentItem) => currentItem.id === item.id
      ? { ...currentItem, status: 'ready' }
      : currentItem));
  }, [processImage, setItemError, t, updateItems]);

  const handleFiles = useCallback((files: File[]) => {
    if (disabled || files.length === 0) return;
    if (itemsRef.current.length + files.length > CAROUSEL_MAX_ITEMS) {
      setOverflowFiles((current) => [...current, ...files.map((file) => file.name)]);
      onOverflowRef.current?.();
      return;
    }
    const additions = files.map((file, index): CarouselMediaItem => {
      return {
        id: `${Date.now()}-${itemsRef.current.length + index}`,
        file,
        fileType: getFileType(file),
        previewUrl: createPreviewUrl(file),
        sourceName: file.name,
        status: 'processing',
        isAiGenerated: false,
      };
    });

    updateItems((current) => [...current, ...additions]);
    additions.forEach(processSelectedFile);
  }, [createPreviewUrl, disabled, processSelectedFile, updateItems]);

  useEffect(() => {
    const captionElement = captionRef.current;
    const changeCaption = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const detailObject = typeof detail === 'object' && detail !== null
        ? detail as { value?: unknown; target?: { value?: unknown } }
        : undefined;
      const eventTarget = event.target as (EventTarget & { value?: unknown }) | null;
      const value = typeof detail === 'string'
        ? detail
        : detailObject?.target?.value ?? detailObject?.value ?? eventTarget?.value;
      onCaptionChange?.(typeof value === 'string' ? value : '');
    };
    captionElement?.addEventListener('bdsInput', changeCaption);
    return () => captionElement?.removeEventListener('bdsInput', changeCaption);
  }, [onCaptionChange, showCaption]);

  useEffect(() => {
    const modal = editModalRef.current;
    if (!modal) return undefined;
    const handleModalChange = (event: Event) => {
      const detail = (event as CustomEvent<{ modalStatus?: string }>).detail;
      if (detail?.modalStatus === 'closed') setEditingItemId(null);
    };
    modal.addEventListener('bdsModalChanged', handleModalChange);
    return () => modal.removeEventListener('bdsModalChanged', handleModalChange);
  }, []);

  const moveItem = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= itemsRef.current.length) return;
    updateItems((current) => {
      const reordered = [...current];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered;
    });
  }, [updateItems]);

  const removeItem = useCallback((item: CarouselMediaItem) => {
    revokePreviewUrl(item.previewUrl);
    updateItems((current) => current.filter((currentItem) => currentItem.id !== item.id));
  }, [revokePreviewUrl, updateItems]);

  const removeAllItems = useCallback(() => {
    itemsRef.current.forEach((item) => revokePreviewUrl(item.previewUrl));
    updateItems(() => []);
    setOverflowFiles([]);
    setEditingItemId(null);
  }, [revokePreviewUrl, updateItems]);

  const replaceItem = (id: string, file: File) => {
    const currentItem = itemsRef.current.find((item) => item.id === id);
    if (!currentItem) return;
    revokePreviewUrl(currentItem.previewUrl);
    const nextItem: CarouselMediaItem = {
      ...currentItem,
      file,
      fileType: getFileType(file),
      previewUrl: createPreviewUrl(file),
      sourceName: file.name,
      width: undefined,
      height: undefined,
      colorSpace: undefined,
      error: undefined,
      status: 'processing',
      containerId: undefined,
      containerPayload: undefined,
      containerCreatedAt: undefined,
      retryStatus: undefined,
      retryError: undefined,
    };
    updateItems((current) => current.map((item) => item.id === id ? nextItem : item));
    processSelectedFile(nextItem);
  };

  const handleReplacementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    const targetId = replacementItemIdRef.current;
    event.currentTarget.value = '';
    replacementItemIdRef.current = null;
    if (file && targetId) replaceItem(targetId, file);
  };

  const handleAddMoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    handleFiles(files);
  };

  const handleDropzoneClick = () => {
    if (disabled) return;
    addMoreInputRef.current?.click();
  };

  const handleDropzoneDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (disabled) return;
    setIsDraggingOver(true);
  };

  const handleDropzoneDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDropzoneDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);
    if (disabled) return;
    handleFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const setItemAiGenerated = useCallback((id: string, isAiGenerated: boolean) => {
    updateItems((current) => current.map((item) => item.id === id
      ? {
        ...item,
        isAiGenerated,
        containerId: undefined,
        containerPayload: undefined,
        containerCreatedAt: undefined,
        retryStatus: undefined,
        retryError: undefined,
      }
      : item));
  }, [updateItems]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;

    const handleButtonClick = (event: Event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest<HTMLElement>('[data-carousel-action]');
      if (!button || disabled || button.hasAttribute('disabled')) return;
      const itemId = button.dataset.itemId;

      switch (button.dataset.carouselAction) {
        case 'add-more':
          addMoreInputRef.current?.click();
          break;
        case 'remove-all':
          removeAllItems();
          break;
        case 'replace':
          if (itemId) {
            replacementItemIdRef.current = itemId;
            replaceInputRef.current?.click();
          }
          break;
        case 'remove': {
          const item = itemsRef.current.find((currentItem) => currentItem.id === itemId);
          if (item) removeItem(item);
          break;
        }
        case 'move-up':
        case 'move-down': {
          if (!itemId) break;
          const index = itemsRef.current.findIndex((currentItem) => currentItem.id === itemId);
          moveItem(index, button.dataset.carouselAction === 'move-up' ? -1 : 1);
          break;
        }
        default:
          break;
      }
    };

    const handleSwitchChange = (event: Event) => {
      if (!(event.target instanceof HTMLElement) || disabled) return;
      const itemId = event.target.dataset.itemId;
      const checked = (event as CustomEvent<{ checked?: boolean }>).detail?.checked;
      if (itemId && typeof checked === 'boolean') setItemAiGenerated(itemId, checked);
    };

    root.addEventListener('bdsClick', handleButtonClick);
    root.addEventListener('bdsChange', handleSwitchChange);
    return () => {
      root.removeEventListener('bdsClick', handleButtonClick);
      root.removeEventListener('bdsChange', handleSwitchChange);
    };
  }, [disabled, moveItem, removeAllItems, removeItem, setItemAiGenerated]);

  const confirmEdit = (item: CarouselMediaItem, file: File, colorSpace: ImageColorSpace) => {
    revokePreviewUrl(item.previewUrl);
    const nextItem: CarouselMediaItem = {
      ...item,
      file,
      previewUrl: createPreviewUrl(file),
      width: undefined,
      height: undefined,
      colorSpace,
      error: undefined,
      status: 'processing',
      containerId: undefined,
      containerPayload: undefined,
      retryStatus: undefined,
      retryError: undefined,
    };
    updateItems((current) => current.map((currentItem) => currentItem.id === item.id ? nextItem : currentItem));
    setEditingItemId(null);
    void processImage(item.id, file);
  };

  const editingItem = items.find((item) => item.id === editingItemId && item.fileType === 'image');

  const getRetryStatusMessage = (item: CarouselMediaItem): string => {
    if (item.retryStatus === 'FAILED') return item.retryError || t('carousel.failedItem');
    const processingKey = item.fileType === 'video' ? 'carousel.stillProcessingVideo' : 'carousel.stillProcessingItem';
    return t(processingKey);
  };

  return (
    <div ref={contentRef} className="scheduler-carousel-content" aria-disabled={disabled}>
      {items.length === 0 && (
        <button
          type="button"
          className={`scheduler-carousel-dropzone${isDraggingOver ? ' scheduler-carousel-dropzone--active' : ''}`}
          data-testid="carousel-dropzone"
          disabled={disabled}
          onClick={handleDropzoneClick}
          onDragOver={handleDropzoneDragOver}
          onDragLeave={handleDropzoneDragLeave}
          onDrop={handleDropzoneDrop}
        >
          <bds-icon type="icon" theme="outline" name="upload" size="large" />
          <bds-typo tag="p" variant="fs-14">{t('carousel.dropzoneDescription')}</bds-typo>
        </button>
      )}
      {items.length > 0 && (
        <ol className="scheduler-carousel-items">
          {items.map((item, index) => (
            <li className="scheduler-carousel-item" key={item.id}>
              <div className="scheduler-carousel-item-row">
                {item.fileType === 'image' ? (
                  <button
                    type="button"
                    className="scheduler-carousel-preview-button"
                    data-testid={`carousel-adjust-${item.id}`}
                    aria-label={`${t('carousel.adjustImage')}: ${item.sourceName}`}
                    disabled={disabled || item.status !== 'ready'}
                    onClick={() => setEditingItemId(item.id)}
                  >
                    <img src={item.previewUrl} alt="" />
                  </button>
                ) : (
                  <video src={item.previewUrl} muted preload="metadata" aria-label={item.sourceName} />
                )}
                <div className="scheduler-carousel-item-info">
                  <bds-typo
                    class="scheduler-carousel-item-name"
                    tag="p"
                    variant="fs-14"
                    title={item.sourceName}
                    no-wrap={true}
                  >
                    {item.sourceName}
                  </bds-typo>
                  {items.length >= CAROUSEL_MIN_ITEMS && (
                    <label className="scheduler-carousel-ai-toggle">
                      <bds-switch
                        class="scheduler-carousel-ai-switch"
                        role="switch"
                        aria-label={t('carousel.aiGeneratedForItem', { name: item.sourceName })}
                        checked={item.isAiGenerated === true}
                        disabled={disabled || item.status !== 'ready'}
                        data-carousel-action="toggle-ai"
                        data-item-id={item.id}
                      />
                      <span>{t('carousel.aiGenerated')}</span>
                    </label>
                  )}
                </div>
                <div className="scheduler-carousel-item-actions">
                  <bds-button
                    type="button"
                    class="scheduler-carousel-replace"
                    size="short"
                    variant="tertiary"
                    style={{ backgroundColor: 'var(--color-surface-0, #ffffff)' }}
                    data-carousel-action="replace"
                    data-item-id={item.id}
                    data-testid={`carousel-replace-${item.id}`}
                    aria-label={t('carousel.replaceItemForItem', { name: item.sourceName })}
                    disabled={disabled}
                  >
                    {t('carousel.replaceItem')}
                  </bds-button>
                  <bds-button-icon
                    class="scheduler-carousel-remove"
                    icon="trash"
                    icon-theme="outline"
                    size="short"
                    variant="secondary"
                    data-carousel-action="remove"
                    data-item-id={item.id}
                    data-testid={`carousel-remove-${item.id}`}
                    aria-label={t('carousel.removeItemForItem', { name: item.sourceName })}
                    disabled={disabled}
                  />
                  <div className="scheduler-carousel-order-actions">
                    <bds-button-icon
                      icon="arrow-up"
                      icon-theme="outline"
                      size="short"
                      variant="secondary"
                      data-carousel-action="move-up"
                      data-item-id={item.id}
                      data-testid={`carousel-move-up-${item.id}`}
                      aria-label={t('carousel.moveUp', { name: item.sourceName })}
                      disabled={disabled || index === 0}
                    />
                    <bds-button-icon
                      icon="arrow-down"
                      icon-theme="outline"
                      size="short"
                      variant="secondary"
                      data-carousel-action="move-down"
                      data-item-id={item.id}
                      data-testid={`carousel-move-down-${item.id}`}
                      aria-label={t('carousel.moveDown', { name: item.sourceName })}
                      disabled={disabled || index === items.length - 1}
                    />
                  </div>
                </div>
              </div>
              {item.status === 'processing' && <bds-typo tag="p" variant="fs-12">{t('carousel.processing')}</bds-typo>}
              {item.fileType === 'image' && item.status === 'ready' && (
                <bds-typo tag="p" variant="fs-12">{t('carousel.outputDetails', {
                  width: item.width ?? '', height: item.height ?? '', size: fileSize(item.file.size),
                })}</bds-typo>
              )}
              {item.retryStatus && ['FAILED', 'PROCESSING'].includes(item.retryStatus) && (
                <bds-chip-tag color={item.retryStatus === 'FAILED' ? 'danger' : 'warning'}>
                  {getRetryStatusMessage(item)}
                </bds-chip-tag>
              )}
              {item.error && (
                <>
                  <bds-chip-tag color="danger">{item.error}</bds-chip-tag>
                  <ValidationFeedback
                    validationResult={{ isValid: false, errors: [item.error], warnings: [] }}
                    fileName={item.sourceName}
                  />
                </>
              )}
            </li>
          ))}
        </ol>
      )}
      {items.length > 0 && (
        <div className="scheduler-carousel-list-actions">
          <bds-button
            type="button"
            class="scheduler-carousel-add-more"
            size="short"
            color="content"
            variant="outline"
            icon-left="upload"
            data-carousel-action="add-more"
            disabled={disabled || items.length >= CAROUSEL_MAX_ITEMS}
          >
            {t('carousel.addMoreImages')}
          </bds-button>
          {items.length >= CAROUSEL_MIN_ITEMS && (
            <bds-button
              type="button"
              class="scheduler-carousel-remove-all"
              size="short"
              color="negative"
              variant="outline"
              icon-left="trash"
              data-carousel-action="remove-all"
              data-testid="carousel-remove-all"
              disabled={disabled}
            >
              {t('carousel.removeAllItems')}
            </bds-button>
          )}
        </div>
      )}
      {overflowFiles.map((name, index) => (
        <bds-chip-tag key={`${name}-${index}`} color="danger">{t('carousel.tooManyItems', { name })}</bds-chip-tag>
      ))}
      <input
        ref={addMoreInputRef}
        className="scheduler-carousel-file-input"
        type="file"
        accept={uploadConfig.accept}
        multiple
        aria-label={t('carousel.addMoreImages')}
        onChange={handleAddMoreChange}
      />
      <input
        ref={replaceInputRef}
        className="scheduler-carousel-file-input"
        type="file"
        accept={uploadConfig.accept}
        aria-label={t('carousel.selectReplacement')}
        onChange={handleReplacementChange}
      />
      <bds-modal
        ref={editModalRef as any}
        open={editingItem !== undefined}
        size="dynamic"
        closeButton={true}
        outzoneClose={true}
        enterClose={false}
      >
        {editingItem && (
          <div className="scheduler-carousel-edit-modal">
            <ImageEditor
              sourceFile={editingItem.file}
              imageContext="feed"
              onConfirm={(file, colorSpace) => confirmEdit(editingItem, file, colorSpace)}
            />
          </div>
        )}
      </bds-modal>
      {showCaption && <bds-input
        ref={captionRef as any}
        id="carousel-caption-input"
        label={t('caption')}
        placeholder={t('feedCaptionPlaceholder')}
        value={caption}
        is-textarea={true}
        disabled={disabled}
      />}
    </div>
  );
};
